import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DrawerService } from '../drawer/drawer.service';
import { BasicButtonColors, BasicButtonModes, BasicButtonSizes } from '@app/shared/enums/buttons';
import { UploadFileService } from '@app/shared/services/utils/upload-files.service';
import { UtilService } from '@app/shared/services/utils/util.service';
import { Observable, Subscription, switchMap } from 'rxjs';
import { FileResponse } from '@app/shared/models/fileResponse';
import { FileUploadService } from '@app/shared/services/utils/file-upload.service';
import { fileEntityTypes, FileUploadStatusEnum, IFile } from '../file-upload/constants';
import { ImportError, ImportValidationError } from '@app/shared/models/file-upload-error-response.model';
import { IQuery } from '@app/shared/interfaces/query';
import { IDrawerConfig } from '../drawer/drawer.interface';
import { FeatureFlagService } from '@app/shared/services/feature-flag/feature-flag.service';
import { EFeatureFlags } from '@app/shared/enums/feature-flag';
import { TranslateService } from '@ngx-translate/core';
import { IInstructions } from '../../interfaces/instructions.interface';

@Component({
  selector: 'cm-upload-flow',
  templateUrl: './cm-upload-flow.component.html',
  styleUrls: ['./cm-upload-flow.component.scss'],
})
export class CmUploadFlowComponent implements OnInit, OnDestroy {
  @Input() extension = 'document';

  BasicButtonColors = BasicButtonColors;
  BasicButtonModes = BasicButtonModes;
  BasicButtonSizes = BasicButtonSizes;

  @Input() fileTemplate: string;

  defaultFileSizeLimit = '25';
  errorList: ImportError[];
  pageIndex = 1;
  isLoadingErrors: boolean;
  currentBatch = [];

  @ViewChild('errorsTable', { static: false })
  errorsTableRef: ElementRef<HTMLDivElement>;

  errorStateSub: Subscription;
  fileImportFailed: boolean;
  drawerConfig: IDrawerConfig;
  dataInstructions: IInstructions;
  fileIndex?: number;
  importId: string;

  _fileUploadFailed: boolean;
  _fileHasParsingError: boolean;
  _failedEntityFiles: any = {};
  _fileHasParsingErrorDetail: string;

  featureFlagsNotAllowed: EFeatureFlags[] = [EFeatureFlags.CUSTOMERS];

  constructor(
    public drawerService: DrawerService,
    private uploadFileService: UploadFileService,
    public fileUploadService: FileUploadService,
    private featureFlagService: FeatureFlagService,
    private ts: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.fileUploadService.isFilePending(this.drawerService?.data?.entity)) {
      const { index } = this.fileUploadService.getCurrentFile(this.drawerService?.data?.entity);

      this.fileIndex = index;
    }

    this.errorStateSub = this.fileUploadService.uploadStatus$.subscribe(({ file, uploadStatus }) => {
      const f = this.fileUploadService.getCurrentFile(this.drawerService.data?.entity);
      this.importId = f?.importId;

      // these flags will take the getters values and then they will be used in the template
      // for some reason the getters themselves are not been updated in the template
      this._fileUploadFailed = this.fileUploadFailed;
      this._fileHasParsingError = this.fileHasParsingError;
      this._fileHasParsingErrorDetail = this.fileHasParsingErrorDetail;

      if (this.fileUploadFailed) {
        this._failedEntityFiles[this.drawerService.data?.entity] = file;
        this.loadImportErrorList();
      }

      this.drawerConfig = {
        title: this.drawerService?.data?.title,
        cancelButton: !this.fileUploadFailed
          ? null
          : {
              label: 'string.close',
              clickHandler: () => {
                this.drawerService.close();
              },
            },
        secondaryButton: !this.fileUploadFailed
          ? {
              label: 'string.cancel',
              clickHandler: () => this.onDialogCancel(),
              mode: BasicButtonModes.FLAT,
              overrideDisabled: !!uploadStatus,
              customDisabled: uploadStatus === FileUploadStatusEnum.INPROCESS && !file?.importId,
            }
          : {
              label: 'file-upload.discard-import',
              clickHandler: () => {
                this.fileIndex = null;
                this._failedEntityFiles[this.drawerService.data?.entity] = undefined;
                this.fileUploadService.reset(this.drawerService?.data?.entity);
                this.drawerService.setDisabledButtons(true);
              },
              overrideDisabled: true,
              customDisabled: false,
            },
        primaryButton: {
          label: 'string.import',
          loadingLabel: 'string.importing',
          isLoadingOverrides: true,
          customLoading: this.isLoading,
          clickHandler: () => {
            this.onImport().subscribe();
            this.drawerService.close();
          },
        },
        width: '500px',
      };

      this.setDrawerConfig();
    });
    this.extension = this.drawerService?.data?.extension ?? this.extension;
    const instructions: IInstructions = this.drawerService?.data?.dataInstructions ?? fileEntityTypes[this.drawerService?.data?.entity]?.dataInstructions;
    this.dataInstructions = this.removeBulletsFromInstructions(instructions);
  }

  removeBulletsFromInstructions(instructions: IInstructions): IInstructions {
    const instructionsTranslated = this.ts.instant(instructions.paragraph);
    instructions = {
      ...instructions,
      paragraph: instructionsTranslated,
    };

    for (const featureFlag of this.featureFlagsNotAllowed) {
      if (!this.featureFlagService.evaluate(featureFlag)) {
        instructions = {
          ...instructions,
          paragraph: this.buildInnerHTML(instructions, featureFlag),
        };
      }
    }
    return instructions;
  }

  buildInnerHTML(instructions: IInstructions, featureFlag: EFeatureFlags): string {
    const temp = document.createElement('div');
    temp.innerHTML = instructions.paragraph;
    const liElements = temp.getElementsByTagName('li');
    for (let i = liElements.length - 1; i >= 0; i--) {
      if (featureFlag.toLowerCase().includes(liElements[i].textContent.toLowerCase())) {
        liElements[i].remove();
      }
    }

    return temp.innerHTML;
  }

  ngOnDestroy() {
    this.errorStateSub.unsubscribe();
  }

  loadImportErrorList() {
    this.currentBatch = [];
    const query: IQuery = {
      pageIndex: this.pageIndex,
      pageSize: 10,
    };
    this.isLoadingErrors = true;

    this.fileUploadService
      .getImportErrorList(query, this.importId, this.drawerService?.data?.entity)
      .subscribe((errorList: ImportError[]) => {
        this.currentBatch = errorList;

        if (this.pageIndex > 1) this.errorList = [...this.errorList, ...this.currentBatch];
        else this.errorList = [...this.currentBatch];

        this.isLoadingErrors = false;
      });
  }

  async getTemplate() {
    const entity = this.drawerService?.data?.entity;
    if (entity) {
      const url = await this.uploadFileService.getTemplate(entity);
      UtilService.downloadBlobFile(url, entity, 'csv');
    }
  }

  private setDrawerConfig(): void {
    const disabledButtons =
      this._fileUploadFailed ||
      this.fileUploadService.isUploadingFile(this.drawerService?.data?.entity) ||
      this.fileUploadService.getFiles(this.drawerService?.data?.entity).length === 0;
    this.drawerService.setDisabledButtons(disabledButtons);
    this.drawerService.setDrawerConfiguration(this.drawerConfig);
  }

  setCsv(e): void {
    if (!this.fileUploadIsCanceled) this.drawerService.setDisabledButtons(false);
  }

  removeFile(e): void {
    this.drawerService.setDisabledButtons(true);
  }

  onImport(): Observable<any> {
    const entity = this.drawerService?.data?.entity;
    const entitySingular = this.drawerService?.data?.entitySingular;

    const collection = this.drawerService?.data?.collection;
    const failedEntityFile = this._failedEntityFiles[this.drawerService.data?.entity];

    if (!entity) return;

    let [file] = this.fileUploadService.getFiles(entity);
    file = file || failedEntityFile;

    file.name = UtilService.fileNameSanitization(file.name);
    const modifiedFile = new File([file.fileBinary], file.name, {
      type: file.fileBinary.type,
    });
    file.fileBinary = modifiedFile;

    this.fileIndex = file.index;

    this.fileUploadService.fileProcessingStarted(file.index);

    const isNemsisXml =
      (entitySingular || collection)?.toLowerCase() === 'invoice' && file.name.toLowerCase().endsWith('.xml');
    if (isNemsisXml) {
      return this.uploadFileService
        .uploadNemsisFile(file.fileBinary)
        .pipe(
          switchMap(({ blobName }) =>
            this.uploadFileService.runNemsisImport(blobName, this.drawerService?.data?.sharedValue)
          )
        );
    }

    return this.uploadFileService.initUploadFile(file.fileBinary).pipe(
      switchMap((response: FileResponse) => {
        const { uri } = response;
        return this.uploadFileService.runImport(
          entity,
          uri,
          this.drawerService?.data?.sharedValue,
          collection,
          entitySingular
        );
      })
    );
  }

  parseError(validation: ImportValidationError): string {
    const message = validation.message || validation.Message;
    return message.split(':').length > 1 ? message.split(':')[1].trim() : message;
  }

  //TODO: THIS FUNCTION IS BEING USED IN MANY COMPONENTS, SHOULD BE EXTRACT TO A SHARED FILE OR DIRECTIVE
  scrollBreakPoint(): void {
    const { scrollTop, clientHeight, scrollHeight } = this.errorsTableRef.nativeElement;
    const bottomLimit = scrollTop + clientHeight;
    if (bottomLimit >= scrollHeight - 50 && !this.isLoadingErrors && this.currentBatch.length) {
      this.pageIndex++;
      this.loadImportErrorList();
    }
  }

  showTable(): boolean {
    if (this.isLoadingErrors && !this.fileHasParsingError) {
      return this.pageIndex > 1;
    }

    return this.errorList?.length > 0;
  }

  onDownloadErrors() {
    this.fileUploadService.exportErrors(null, this._failedEntityFiles[this.drawerService.data?.entity]);
  }

  onDialogCancel() {
    this.drawerService.close();
    const file = this.fileUploadService.getCurrentFile(this.drawerService?.data?.entity);
    if (file?.importId) this.uploadFileService.deleteImport(file.importId).subscribe();
    this.fileUploadService.reset(this.drawerService?.data?.entity);
  }

  get isLoading() {
    return this.fileIndex ? this.fileUploadService.isProcessingFile(this.drawerService?.data?.entity) : false;
  }

  get fileUploadFailed(): boolean {
    if (!this.fileIndex || this.fileUploadService.getFiles(this.drawerService?.data?.entity).length === 0) return false;

    const file = this.fileUploadService.getFile(this.fileIndex);

    return file?.status === FileUploadStatusEnum.FAILED && file?.fileEntity.entity === this.drawerService?.data?.entity;
  }

  get fileUploadIsCanceled() {
    return this.fileIndex ? this.fileUploadService.uploadIsCanceled(this.fileIndex) : false;
  }

  get fileHasParsingError() {
    return this.fileIndex ? this.fileUploadService.getFile(this.fileIndex)?.hasParsingError : false;
  }

  get fileHasParsingErrorDetail() {
    return this.fileIndex ? this.fileUploadService.getFile(this.fileIndex)?.hasParsingErrorDetail : null;
  }
}
